/**
 * RizeHer Health Assessment Quiz Engine
 * Standalone. Do NOT merge into theme.js
 * Steps: 1 Welcome, 2 Concern, 3 Symptoms, 4 Cycle, 5 Lifestyle, 6 Supplements, 7 Email, Results
 */
(function () {
  'use strict';

  // ============================================================
  // QUIZ CONFIGURATION (Liquid-injected metafield + hardcoded fallback)
  // ============================================================
  var HARDCODED_FALLBACK_CONFIG = {
    version: '1.0',
    concern_mappings: {
      pcos: {
        primary_handle: 'pcos-balance',
        primary_name: 'PCOS Balance',
        primary_fallback_price: 2499,
        insight_body: 'PCOS affects 1 in 5 Indian women, and the root cause is often insulin resistance. Clinical-dose Myo-Inositol (4 g/day in PCOS Balance) is the gold-standard intervention validated in 12+ randomised controlled trials.',
        supporting_handles: ['cyclesync-28', 'ironlift'],
        triggers: {
          irregular_period: {
            keywords: ['irregular', 'missed period', 'infrequent'],
            note: 'Since you experience irregular or missed periods, start with PCOS Balance to restore hormonal balance first. Once your cycles become more regular (typically 2 to 3 months), consider adding CycleSync 28 to optimise each phase of your cycle.'
          }
        }
      },
      periods: {
        primary_handle: 'flowease',
        primary_name: 'FlowEase Essential',
        primary_fallback_price: 599,
        insight_body: 'Period pain is caused by prostaglandins, chemicals that make your uterus contract. The BCM-95 Curcumin and Ginger in FlowEase are clinically studied to reduce prostaglandin levels as effectively as ibuprofen.',
        supporting_handles: ['cyclecalm', 'ironlift']
      },
      pms: {
        primary_handle: 'cyclecalm',
        primary_name: 'CycleCalm',
        primary_fallback_price: 1299,
        insight_body: 'PMS is driven by hormonal shifts in the luteal phase (Days 18 to 28). The KSM-66 Ashwagandha and Saffron in CycleCalm are clinically studied to support mood balance and reduce cortisol during this phase.',
        supporting_handles: ['cyclesync-28', 'flowease']
      },
      energy: {
        primary_handle: 'ironlift',
        primary_name: 'IronLift',
        primary_fallback_price: 899,
        insight_body: '57% of Indian women are anaemic (NFHS-5). Iron deficiency causes fatigue, brain fog, and breathlessness. IronLift uses Iron Bisglycinate, which absorbs 3.4x better than ferrous sulfate with Indian diets.',
        supporting_handles: ['flowease', 'cyclecalm']
      },
      hair: {
        primary_handle: 'pcos-balance',
        primary_name: 'PCOS Balance',
        primary_fallback_price: 2499,
        insight_body: 'Hair fall in women is often hormonal, linked to PCOS, iron deficiency, or thyroid issues. PCOS Balance addresses the hormonal root cause with clinical-dose Myo-Inositol and Zinc. IronLift supports iron levels, one of the most overlooked causes of hair fall.',
        supporting_handles: ['ironlift', 'cyclecalm']
      },
      wellness: {
        primary_handle: 'cyclesync-28',
        primary_name: 'CycleSync 28',
        primary_fallback_price: 1999,
        insight_body: "CycleSync 28 is India's first cycle-syncing supplement. Four phase-specific formulas with 24 targeted ingredients that change with your cycle, because your hormones on Day 3 are different from Day 14.",
        supporting_handles: ['ironlift', 'cyclecalm']
      }
    },
    bundle_discount: { enabled: false },
    age_routing: { enabled: false }
  };

  var quizConfig = HARDCODED_FALLBACK_CONFIG;
  (function () {
    try {
      var raw = window.rizeherQuizConfig;
      if (raw === null || raw === undefined) return;
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (!parsed || !parsed.version) {
        console.warn('[RizeHer Quiz] Config missing version field; using fallback');
        return;
      }
      quizConfig = parsed;
    } catch (err) {
      console.warn('[RizeHer Quiz] Config parse failed; using fallback:', err.message);
    }
  }());

  // ============================================================
  // STATE
  // ============================================================
  var currentStep = 1;
  var TOTAL_STEPS = 7;

  var quizData = {
    name: '',
    concerns: [],         // array of 1-3 concern keys
    symptoms: [],         // array of selected symptom strings
    cycleLength: '',
    periodDuration: '',
    flowIntensity: '',
    diet: '',
    exercise: '',
    stress: '',
    sleep: '',
    age: '',
    currentSupplements: [], // array
    email: '',
    phone: '',
    newsletter: true
  };

  // ============================================================
  // DIET & LIFESTYLE TIPS DATABASE
  // ============================================================
  var dietTips = {
    'pcos': [
      {
        emoji: '',
        tip: 'Eat low-glycemic foods',
        detail: 'Choose whole grains, legumes, and non-starchy vegetables to manage insulin resistance, the root driver of PCOS in most women.'
      },
      {
        emoji: '',
        tip: 'Add anti-inflammatory spices daily',
        detail: 'Turmeric (1 tsp/day) and cinnamon (half tsp in oats) significantly reduce PCOS-related inflammation and insulin spikes.'
      },
      {
        emoji: '',
        tip: 'Protein at every meal',
        detail: 'Include dal, paneer, eggs, or nuts at every meal. Protein stabilizes blood sugar and reduces excess androgen production.'
      },
      {
        emoji: '',
        tip: 'Walk 10 minutes after every meal',
        detail: 'Post-meal walking is the #1 lifestyle intervention for insulin resistance. Even short 10-minute walks improve glucose disposal by 22%.'
      }
    ],
    'periods': [
      {
        emoji: '',
        tip: 'Omega-3 rich foods 3 days before your period',
        detail: 'Flaxseeds, walnuts, and fatty fish reduce prostaglandins, the chemicals that cause uterine cramping. Start 3 to 4 days before Day 1.'
      },
      {
        emoji: '',
        tip: 'Magnesium from dark chocolate and seeds',
        detail: 'Dark chocolate (70%+), spinach, and pumpkin seeds are rich in magnesium, a natural muscle relaxant. Aim for 300 to 400mg/day.'
      },
      {
        emoji: '',
        tip: 'Hydrate with warm water and herbal teas',
        detail: 'Drink 2.5 to 3L water/day. Dehydration intensifies uterine contractions. Warm ginger or chamomile tea reduces cramping.'
      },
      {
        emoji: '',
        tip: 'Avoid these 3 days before your period',
        detail: 'Red meat, refined sugar, and processed foods spike prostaglandins. Eliminate them on Days 25 to 28 of your cycle for dramatic relief.'
      }
    ],
    'pms': [
      {
        emoji: '',
        tip: 'Complex carbs in the luteal phase',
        detail: 'Brown rice, sweet potato, and oats boost serotonin during Days 18 to 28. Low serotonin is the direct driver of PMS mood symptoms.'
      },
      {
        emoji: '',
        tip: 'Reduce sodium 7 to 10 days before your period',
        detail: 'High sodium causes water retention and bloating that amplify PMS discomfort. Cut processed foods and added salt significantly.'
      },
      {
        emoji: '',
        tip: 'Calcium-rich foods reduce PMS by 48%',
        detail: 'Dairy, ragi, and sesame seeds (aim for 1200mg calcium/day) reduce PMS severity. Clinically validated in a landmark AJOG study.'
      },
      {
        emoji: '',
        tip: 'Limit caffeine and alcohol Days 18 to 28',
        detail: 'Caffeine and alcohol spike cortisol and deplete magnesium during your luteal phase. Both directly worsen anxiety and mood swings.'
      }
    ],
    'energy': [
      {
        emoji: '',
        tip: 'Iron-rich foods every single day',
        detail: 'Spinach, rajma, jaggery, and sesame seeds. Always pair with Vitamin C (lemon juice) to triple non-heme iron absorption.'
      },
      {
        emoji: '',
        tip: 'Never drink tea or coffee with meals',
        detail: 'Tannins in tea and coffee block iron absorption by up to 60%. Wait at least 1 to 2 hours after meals before having these beverages.'
      },
      {
        emoji: '',
        tip: 'B12 and folate from eggs and dairy daily',
        detail: 'Vitamin B12 deficiency causes fatigue, brain fog, and nerve symptoms. Eggs, dairy, and fortified cereals are your best daily sources.'
      },
      {
        emoji: '',
        tip: 'Eat every 3 to 4 hours without fail',
        detail: 'Small, protein-rich meals every 3 to 4 hours prevent blood sugar crashes that cause afternoon fatigue. Never skip breakfast.'
      }
    ],
    'hair': [
      {
        emoji: '',
        tip: 'Biotin-rich foods support keratin production',
        detail: 'Eggs (especially the yolk), almonds, and sweet potatoes provide biotin, essential for keratin synthesis and follicle health.'
      },
      {
        emoji: '',
        tip: 'Iron + Vitamin C is the gold combo',
        detail: 'Spinach + lemon juice daily maximizes iron absorption. Iron deficiency is responsible for up to 72% of female hair loss cases in India.'
      },
      {
        emoji: '',
        tip: 'Protein is non-negotiable: hair is 95% keratin',
        detail: 'Aim for 0.8 to 1g protein per kg body weight daily. Dal, paneer, eggs, and nuts every day. Deficiency causes diffuse, progressive thinning.'
      },
      {
        emoji: '',
        tip: 'Zinc from pumpkin seeds regulates DHT',
        detail: 'Pumpkin seeds, chickpeas, and cashews provide zinc, which regulates DHT, the hormone that causes follicle miniaturization and shedding.'
      }
    ],
    'wellness': [
      {
        emoji: '',
        tip: 'Ashwagandha in warm milk at bedtime',
        detail: "KSM-66 Ashwagandha (300 to 600mg daily) reduces cortisol by 27% over 8 weeks. Clinically validated. It's the foundation of CycleCalm."
      },
      {
        emoji: '',
        tip: 'Eat 5+ colors every day',
        detail: 'Each color represents unique phytonutrients: red (lycopene), orange (beta-carotene), purple (anthocyanins). Diversity powers immunity.'
      },
      {
        emoji: '',
        tip: 'Fermented foods for gut health',
        detail: 'Curd, idli batter, kanji, and kimchi feed your microbiome. 70% of your immune system lives in your gut. A healthy gut means balanced hormones.'
      },
      {
        emoji: '',
        tip: 'Optimize your sleep with food',
        detail: 'Tart cherry juice or 2 kiwis 1 hour before bed increases melatonin naturally. Magnesium (from pumpkin seeds) deepens sleep quality.'
      }
    ]
  };

  // ============================================================
  // CONCERN DISPLAY NAMES
  // ============================================================
  var concernNames = {
    'pcos': 'PCOS & Hormones',
    'periods': 'Period Pain & Cramps',
    'pms': 'PMS & Mood Swings',
    'energy': 'Energy & Iron',
    'hair': 'Hair Fall & Thinning',
    'wellness': 'General Wellness'
  };

  // ============================================================
  // DOM REFERENCES
  // ============================================================
  var steps, progressFill, backBtn, quizResults, progressBar;

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    steps = document.querySelectorAll('.quiz-step');
    progressFill = document.getElementById('quizProgressFill');
    progressBar = document.getElementById('quizProgress');
    backBtn = document.getElementById('quizBackBtn');
    quizResults = document.getElementById('quizResults');

    if (!steps.length || !progressFill || !quizResults) return;

    // Next button click listeners
    document.querySelectorAll('[data-step-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.getAttribute('data-step-next'), 10);
        if (validateStep(step)) {
          collectStepData(step);
          goNext();
        }
      });
    });

    // Back button
    backBtn.addEventListener('click', goBack);

    // Submit button
    var submitBtn = document.getElementById('quizSubmitBtn');
    if (submitBtn) {
      submitBtn.addEventListener('click', submitQuiz);
    }

    // Concern card selection (multi-select)
    document.querySelectorAll('.quiz-card').forEach(function (card) {
      card.addEventListener('click', function () { toggleConcern(card); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleConcern(card);
        }
      });
    });

    // Concern continue button
    var concernContinueBtn = document.getElementById('concernContinueBtn');
    if (concernContinueBtn) {
      concernContinueBtn.addEventListener('click', function () {
        if (validateStep(2)) {
          collectStepData(2);
          goNext();
        }
      });
    }

    // Supplement "None" toggle
    setupNoneToggle();

    // Initialise progress
    updateProgress(1);
  }

  // ============================================================
  // CONCERN CARD SELECTION (MULTI-SELECT, 1-3)
  // ============================================================
  function toggleConcern(card) {
    var concern = card.getAttribute('data-concern');
    var idx = quizData.concerns.indexOf(concern);
    if (idx > -1) {
      quizData.concerns.splice(idx, 1);
      card.classList.remove('quiz-card--selected');
      card.setAttribute('aria-checked', 'false');
    } else {
      if (quizData.concerns.length >= 3) {
        showToast('Maximum 3 concerns');
        return;
      }
      quizData.concerns.push(concern);
      card.classList.add('quiz-card--selected');
      card.setAttribute('aria-checked', 'true');
    }
    updateConcernCounter();
    hideError('concernError');
  }

  function updateConcernCounter() {
    var counter = document.getElementById('concernCounter');
    if (counter) counter.textContent = quizData.concerns.length + ' of 3 selected';
    var continueBtn = document.getElementById('concernContinueBtn');
    if (continueBtn) continueBtn.disabled = quizData.concerns.length === 0;
  }

  function showToast(msg) {
    var existing = document.querySelector('.quiz-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'quiz-toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    setTimeout(function () { if (toast.parentNode) toast.remove(); }, 3000);
  }

  function hideDuplicateSymptoms() {
    var seen = {};
    document.querySelectorAll('.quiz-symptom-group:not([hidden]) .quiz-checkbox-label').forEach(function (label) {
      var cb = label.querySelector('input[type="checkbox"]');
      if (!cb) return;
      var val = cb.value.toLowerCase().trim();
      if (seen[val]) {
        label.style.display = 'none';
        cb.checked = false;
      } else {
        label.style.display = '';
        seen[val] = true;
      }
    });
  }

  // ============================================================
  // SUPPLEMENT "NONE" TOGGLE
  // ============================================================
  function setupNoneToggle() {
    var noneCheckbox = document.querySelector('.quiz-supp-none');
    var otherCheckboxes = document.querySelectorAll('.quiz-supp-cb:not(.quiz-supp-none)');

    if (!noneCheckbox) return;

    noneCheckbox.addEventListener('change', function () {
      if (noneCheckbox.checked) {
        otherCheckboxes.forEach(function (cb) { cb.checked = false; });
      }
    });

    otherCheckboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        if (cb.checked) noneCheckbox.checked = false;
      });
    });
  }

  // ============================================================
  // NAVIGATION
  // ============================================================
  function goNext() {
    // When leaving Step 2, show symptom groups for ALL selected concerns
    if (currentStep === 2) {
      document.querySelectorAll('.quiz-symptom-group').forEach(function (g) {
        g.hidden = true;
      });
      quizData.concerns.forEach(function (concern) {
        var group = document.querySelector('.quiz-symptom-group[data-concern-group="' + concern + '"]');
        if (group) {
          group.hidden = false;
          // Add heading if not already present
          if (!group.querySelector('.quiz-symptom-group__heading')) {
            var heading = document.createElement('h3');
            heading.className = 'quiz-symptom-group__heading';
            heading.textContent = concernNames[concern] || concern;
            group.insertBefore(heading, group.firstChild);
          }
        }
      });
      hideDuplicateSymptoms();
    }

    currentStep += 1;
    showStep(currentStep);
    updateProgress(currentStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    currentStep -= 1;
    showStep(currentStep);
    updateProgress(currentStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function showStep(n) {
    steps.forEach(function (s) { s.hidden = true; });
    var target = document.querySelector('.quiz-step[data-step="' + n + '"]');
    if (target) target.hidden = false;

    backBtn.hidden = (n === 1);
    quizResults.hidden = true;
    if (progressBar) progressBar.hidden = false;
  }

  function updateProgress(n) {
    var pct = Math.round((n / TOTAL_STEPS) * 100);
    progressFill.style.width = pct + '%';
  }

  // ============================================================
  // VALIDATION
  // ============================================================
  function validateStep(step) {
    switch (step) {
      case 1: {
        var nameVal = document.getElementById('quizName');
        if (!nameVal || nameVal.value.trim().length < 2) {
          showError('nameError');
          return false;
        }
        hideError('nameError');
        return true;
      }
      case 2: {
        if (quizData.concerns.length === 0) {
          showError('concernError');
          return false;
        }
        hideError('concernError');
        return true;
      }
      case 3: {
        var allChecked = document.querySelectorAll('.quiz-symptom-group:not([hidden]) input[type="checkbox"]:checked');
        if (allChecked.length === 0) {
          showError('symptomsError');
          return false;
        }
        hideError('symptomsError');
        return true;
      }
      case 4: {
        var ageEl = document.getElementById('quizAge');
        if (ageEl && !ageEl.value) {
          showError('ageError');
          return false;
        }
        hideError('ageError');
        var cl = document.getElementById('quizCycleLength');
        var pd = document.getElementById('quizPeriodDuration');
        var fi = document.getElementById('quizFlowIntensity');
        if (!cl || !cl.value || !pd || !pd.value || !fi || !fi.value) {
          showError('cycleError');
          return false;
        }
        hideError('cycleError');
        return true;
      }
      case 5: {
        var diet = document.getElementById('quizDiet');
        var exercise = document.getElementById('quizExercise');
        var stress = document.getElementById('quizStress');
        var sleep = document.getElementById('quizSleep');
        if (!diet || !diet.value || !exercise || !exercise.value ||
            !stress || !stress.value || !sleep || !sleep.value) {
          showError('lifestyleError');
          return false;
        }
        hideError('lifestyleError');
        return true;
      }
      case 6:
        // No minimum requirement for supplements
        return true;
      default:
        return true;
    }
  }

  function showError(id) {
    var el = document.getElementById(id);
    if (el) el.hidden = false;
  }

  function hideError(id) {
    var el = document.getElementById(id);
    if (el) el.hidden = true;
  }

  // ============================================================
  // DATA COLLECTION
  // ============================================================
  function collectStepData(step) {
    switch (step) {
      case 1: {
        var nameEl = document.getElementById('quizName');
        quizData.name = nameEl ? nameEl.value.trim() : '';
        pushEvent('quiz_start', { name: quizData.name });
        break;
      }
      case 2: {
        // concerns already set by toggleConcern()
        pushEvent('quiz_concern_selected', { concerns: quizData.concerns.join(',') });
        break;
      }
      case 3: {
        quizData.symptoms = Array.from(
          document.querySelectorAll('.quiz-symptom-group:not([hidden]) input[type="checkbox"]:checked')
        ).map(function (cb) { return cb.value; });
        break;
      }
      case 4: {
        var ageSelectEl = document.getElementById('quizAge');
        quizData.age = ageSelectEl ? ageSelectEl.value : '';
        var clEl = document.getElementById('quizCycleLength');
        var pdEl = document.getElementById('quizPeriodDuration');
        var fiEl = document.getElementById('quizFlowIntensity');
        quizData.cycleLength = clEl ? clEl.value : '';
        quizData.periodDuration = pdEl ? pdEl.value : '';
        quizData.flowIntensity = fiEl ? fiEl.value : '';
        break;
      }
      case 5: {
        var dEl = document.getElementById('quizDiet');
        var eEl = document.getElementById('quizExercise');
        var sEl = document.getElementById('quizStress');
        var slEl = document.getElementById('quizSleep');
        quizData.diet = dEl ? dEl.value : '';
        quizData.exercise = eEl ? eEl.value : '';
        quizData.stress = sEl ? sEl.value : '';
        quizData.sleep = slEl ? slEl.value : '';
        break;
      }
      case 6: {
        quizData.currentSupplements = Array.from(
          document.querySelectorAll('.quiz-supp-cb:checked')
        ).map(function (cb) { return cb.value; });
        break;
      }
    }
  }

  // ============================================================
  // SUBMIT (Step 7)
  // ============================================================
  function submitQuiz() {
    var emailEl = document.getElementById('quizEmail');
    var email = emailEl ? emailEl.value.trim() : '';
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      showError('emailError');
      return;
    }
    hideError('emailError');

    var phoneEl = document.getElementById('quizPhone');
    var newsletterEl = document.getElementById('quizNewsletter');

    quizData.email = email;
    quizData.phone = phoneEl ? phoneEl.value.trim() : '';
    quizData.newsletter = newsletterEl ? newsletterEl.checked : true;

    // Persist to localStorage for Klaviyo / retargeting
    try {
      localStorage.setItem('rizeher_quiz', JSON.stringify(quizData));
    } catch (e) { /* storage unavailable */ }

    pushEvent('quiz_complete', {
      concerns: quizData.concerns.join(','),
      email: quizData.email,
      symptoms_count: quizData.symptoms.length
    });

    renderResults();
  }

  // ============================================================
  // MULTI-CONCERN RECOMMENDATION ENGINE
  // ============================================================
  function applyAgeRouting(concern, baseRec, userAge) {
    if (!quizConfig.age_routing || !quizConfig.age_routing.enabled) return baseRec;
    if (!userAge) return baseRec;
    var bracketRules = quizConfig.age_routing[userAge];
    if (!bracketRules) return baseRec;
    var concernRules = bracketRules[concern];
    if (!concernRules) return baseRec;

    var rec = JSON.parse(JSON.stringify(baseRec));
    if (concernRules.override_insight) rec.insight_body = concernRules.override_insight;
    if (concernRules.swap_primary) {
      rec.primary_handle = concernRules.swap_primary;
      rec.primary_name = concernRules.swap_primary_name || concernRules.swap_primary;
    }
    if (concernRules.swap_primary_price) rec.primary_fallback_price = concernRules.swap_primary_price;
    if (concernRules.add_supporting) {
      var existing = rec.supporting_handles || [];
      var toAdd = concernRules.add_supporting;
      toAdd.forEach(function (h) {
        if (existing.indexOf(h) === -1) existing.push(h);
      });
      rec.supporting_handles = existing;
    }
    return rec;
  }

  function getMultiConcernRecommendation() {
    var concerns = quizData.concerns;
    if (concerns.length === 0) return null;

    // Collect primary handles from each concern, deduplicate
    var handleSet = {};
    var productList = [];
    concerns.forEach(function (c) {
      var baseRec = quizConfig.concern_mappings[c];
      if (!baseRec) return;
      var rec = applyAgeRouting(c, baseRec, quizData.age);
      if (!handleSet[rec.primary_handle]) {
        handleSet[rec.primary_handle] = true;
        productList.push({
          handle: rec.primary_handle,
          name: rec.primary_name || rec.primary_handle,
          price: rec.primary_fallback_price
        });
      }
    });

    // Cap at 3 products
    productList = productList.slice(0, 3);

    // Build combined insight from first concern
    var primaryConcern = concerns[0];
    var primaryBaseRec = quizConfig.concern_mappings[primaryConcern];
    var primaryRec = primaryBaseRec ? applyAgeRouting(primaryConcern, primaryBaseRec, quizData.age) : null;
    var insight = primaryRec ? primaryRec.insight_body : '';

    // Check for PCOS + irregular/missed periods symptom
    var irregularNote = '';
    var hasIrregularPeriods = quizData.symptoms.some(function (s) {
      return s.toLowerCase().indexOf('irregular') > -1 || s.toLowerCase().indexOf('missed period') > -1;
    });
    var pcosMapping = quizConfig.concern_mappings.pcos;
    if (
      concerns.indexOf('pcos') > -1 &&
      hasIrregularPeriods &&
      pcosMapping &&
      pcosMapping.triggers &&
      pcosMapping.triggers.irregular_period
    ) {
      irregularNote = pcosMapping.triggers.irregular_period.note || '';
    }

    return {
      products: productList,
      insight: insight,
      concerns: concerns,
      irregularNote: irregularNote
    };
  }

  // ============================================================
  // RESULTS RENDERING
  // ============================================================
  function renderResults() {
    // Hide form elements
    steps.forEach(function (s) { s.hidden = true; });
    backBtn.hidden = true;
    if (progressBar) progressBar.hidden = true;

    quizResults.hidden = false;
    quizResults.innerHTML = '<div class="quiz-results__loading"><p>Analysing your profile\u2026</p></div>';

    var multiRec = getMultiConcernRecommendation();
    if (!multiRec) {
      quizResults.innerHTML = '<p style="text-align:center;padding:40px;">Something went wrong. Please refresh and try again.</p>';
      return;
    }

    // Fetch live products (all 5 hero SKUs)
    var liveHandles = ['pcos-balance', 'flowease', 'cyclecalm', 'cyclesync-28', 'ironlift'];
    var fetches = multiRec.products.map(function (p) {
      if (liveHandles.indexOf(p.handle) > -1) {
        return fetchProduct(p.handle).catch(function () { return null; });
      }
      return Promise.resolve(null); // draft product, use hardcoded data
    });

    Promise.all(fetches).then(function (fetchedProducts) {
      buildMultiResultsHTML(multiRec, fetchedProducts);
    });
  }

  function buildMultiResultsHTML(multiRec, fetchedProducts) {
    var primaryConcern = multiRec.concerns[0];
    var tips = dietTips[primaryConcern] || [];

    // Build concern labels
    var concernLabels = multiRec.concerns.map(function (c) {
      return concernNames[c] || c;
    });
    var waText = 'Hi! I just took the RizeHer quiz. My concerns are ' + concernLabels.join(', ') + '. Can you help with my personalized plan?';

    var reviewedByText = quizResults.dataset.reviewedByText || '';

    var html = '';

    // 1. Greeting and insight
    html += '<div class="quiz-results__greeting">';
    html += '<p class="quiz-results__hi">Hi ' + escHtml(quizData.name) + ',</p>';
    html += '<h2 class="quiz-results__title">Here\'s your wellness profile</h2>';
    html += '<div class="quiz-results__concern-badges">';
    for (var b = 0; b < concernLabels.length; b++) {
      html += '<span class="quiz-results__concern-badge">' + escHtml(concernLabels[b]) + '</span>';
    }
    html += '</div>';
    html += '<p class="quiz-results__insight">' + escHtml(multiRec.insight) + '</p>';
    if (multiRec.irregularNote) {
      html += '<p class="quiz-results__irregular-note">' + escHtml(multiRec.irregularNote) + '</p>';
    }
    html += '</div>';

    // Trust signal
    if (reviewedByText) {
      html += '<p class="quiz-results__reviewed-by">' + escHtml(reviewedByText) + '</p>';
    }

    // 2. Product recommendations (Your Personalized Stack)
    html += '<div class="quiz-results__section">';
    html += '<h3 class="quiz-results__section-title">Your Personalized Stack</h3>';
    html += '<div class="quiz-results__stack">';
    for (var i = 0; i < multiRec.products.length; i++) {
      html += buildProductCardHTML(fetchedProducts[i], multiRec.products[i]);
    }
    html += '</div></div>';

    // 3. Diet tips
    if (tips.length) {
      html += '<div class="quiz-results__section quiz-results__diet-tips">';
      html += '<h3 class="quiz-results__section-title">Personalized Diet & Lifestyle Tips</h3>';
      html += '<div class="quiz-tips-grid">';
      for (var t = 0; t < tips.length; t++) {
        html += '<div class="quiz-tip-card">';
        html += '<div class="quiz-tip-card__content">';
        html += '<strong class="quiz-tip-card__title">' + escHtml(tips[t].tip) + '</strong>';
        html += '<p class="quiz-tip-card__detail">' + escHtml(tips[t].detail) + '</p>';
        html += '</div></div>';
      }
      html += '</div></div>';
    }

    // 4. WhatsApp CTA
    html += '<div class="quiz-results__whatsapp">';
    html += '<p class="quiz-results__whatsapp-text">Want expert guidance on your personalized formula?</p>';
    html += '<a class="quiz-btn-whatsapp" href="https://wa.me/918699228694?text=' + encodeURIComponent(waText) + '" target="_blank" rel="noopener noreferrer">';
    html += 'Chat with Our Wellness Advisor';
    html += '</a></div>';

    quizResults.innerHTML = html;

    // Attach ATC listeners after rendering
    quizResults.querySelectorAll('[data-atc-variant]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        addToCart(btn.getAttribute('data-atc-variant'), btn.getAttribute('data-product-name'), btn);
      });
    });

    // Scroll into view
    quizResults.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function buildProductCardHTML(productData, staticData) {
    var imgSrc = getProductImage(productData);
    var variantId = getFirstVariantId(productData);
    var price = getProductPrice(productData, staticData.price);
    var url = '/products/' + staticData.handle;
    var name = staticData.name;
    var isLive = !!variantId;

    var html = '<div class="quiz-results__product-card">';
    if (imgSrc) {
      html += '<a href="' + url + '"><img src="' + imgSrc + '" alt="' + escHtml(name) + '" class="quiz-results__product-img" loading="lazy" width="120" height="120"></a>';
    } else {
      html += '<div class="quiz-results__product-placeholder"><span>' + escHtml(name.charAt(0)) + '</span></div>';
    }
    html += '<div class="quiz-results__product-info">';
    html += '<h4 class="quiz-results__product-name"><a href="' + url + '">' + escHtml(name) + '</a></h4>';
    html += '<p class="quiz-results__product-price">₹' + formatPrice(price) + '</p>';
    if (isLive) {
      html += '<button class="quiz-btn-primary quiz-btn-atc" data-atc-variant="' + variantId + '" data-product-name="' + escHtml(name) + '">Add to Ritual</button>';
    } else {
      html += '<a href="' + url + '" class="quiz-btn-secondary">View Product</a>';
    }
    html += '</div></div>';
    return html;
  }

  // ============================================================
  // PRODUCT DATA HELPERS
  // ============================================================
  function getProductImage(productData) {
    return productData && productData.image ? productData.image.src : '';
  }

  function getFirstVariantId(productData) {
    return productData && productData.variants && productData.variants[0]
      ? String(productData.variants[0].id)
      : '';
  }

  function getProductPrice(productData, fallback) {
    if (productData && productData.variants && productData.variants[0]) {
      var p = parseFloat(productData.variants[0].price);
      if (p > 10000) p = p / 100; // Shopify may return paise
      return Math.round(p);
    }
    return fallback;
  }

  // ============================================================
  // SHOPIFY AJAX API
  // ============================================================
  function fetchProduct(handle) {
    return fetch('/products/' + handle + '.json')
      .then(function (res) {
        if (!res.ok) throw new Error('Product not found: ' + handle);
        return res.json();
      })
      .then(function (json) { return json.product; });
  }

  function addToCart(variantId, productName, btn) {
    var originalText = btn.textContent;
    btn.textContent = 'Adding\u2026';
    btn.disabled = true;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed');
        return res.json();
      })
      .then(function () {
        btn.textContent = 'Added \u2713';
        btn.classList.add('quiz-btn-atc--success');
        pushEvent('quiz_atc', { variant_id: variantId, product_name: productName });
        // Open + refresh the cart drawer (CartDrawer listens for cart:open in theme.js)
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(function () {
        btn.textContent = originalText;
        btn.disabled = false;
      });
  }

  // ============================================================
  // ANALYTICS
  // ============================================================
  function pushEvent(eventName, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: eventName };
      Object.keys(data).forEach(function (k) { payload[k] = data[k]; });
      window.dataLayer.push(payload);
    } catch (e) { /* GTM not loaded */ }
  }

  // ============================================================
  // UTILITIES
  // ============================================================
  function escHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str || '')));
    return div.innerHTML;
  }

  function formatPrice(num) {
    return parseInt(num, 10).toLocaleString('en-IN');
  }

  // ============================================================
  // BOOT
  // ============================================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
